import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { RevoltModal } from '../../../../../components/RevoltModal';

function ZoomPanChart({ children, className = '' }) {
  const containerRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const lastRef = useRef({ touches: [], scale: 1, translate: { x: 0, y: 0 } });

  const stateRef = useRef({ scale: 1, translate: { x: 0, y: 0 } });
  stateRef.current = { scale, translate };

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;

    const wheelHandler = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();

      const container = containerRef.current;
      if (!container) return;

      const { scale: currentScale, translate: currentTranslate } = stateRef.current;
      const rect = container.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const delta = e.deltaY > 0 ? -0.15 : 0.15;
      const nextScale = Math.min(4, Math.max(0.5, currentScale + delta));
      const contentX = (centerX - currentTranslate.x) / currentScale;
      const contentY = (centerY - currentTranslate.y) / currentScale;

      setTranslate({
        x: centerX - contentX * nextScale,
        y: centerY - contentY * nextScale,
      });
      setScale(nextScale);
    };

    el.addEventListener('wheel', wheelHandler, { passive: false });
    return () => el.removeEventListener('wheel', wheelHandler);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const touchStart = (e) => {
      if (e.touches.length === 0) return;

      lastRef.current = {
        touches: Array.from(e.touches).map((touch) => ({ x: touch.clientX, y: touch.clientY })),
        scale: stateRef.current.scale,
        translate: { ...stateRef.current.translate },
      };
    };

    const touchMove = (e) => {
      e.preventDefault();
      if (e.touches.length === 0) return;

      const last = lastRef.current;
      const rect = el.getBoundingClientRect();
      let nextScale = last.scale;
      let nextTranslate = { ...last.translate };

      if (e.touches.length >= 2) {
        const touchA = e.touches[0];
        const touchB = e.touches[1];
        const distance = Math.hypot(touchB.clientX - touchA.clientX, touchB.clientY - touchA.clientY);
        const centerX = (touchA.clientX + touchB.clientX) / 2 - rect.left;
        const centerY = (touchA.clientY + touchB.clientY) / 2 - rect.top;

        if (last.touches.length >= 2) {
          const lastDistance = Math.hypot(
            last.touches[1].x - last.touches[0].x,
            last.touches[1].y - last.touches[0].y
          );
          const lastCenterX = (last.touches[0].x + last.touches[1].x) / 2 - rect.left;
          const lastCenterY = (last.touches[0].y + last.touches[1].y) / 2 - rect.top;

          if (lastDistance > 0) {
            nextScale = Math.min(4, Math.max(0.5, last.scale * (distance / lastDistance)));
            const contentX = (lastCenterX - last.translate.x) / last.scale;
            const contentY = (lastCenterY - last.translate.y) / last.scale;
            nextTranslate = {
              x: centerX - contentX * nextScale,
              y: centerY - contentY * nextScale,
            };
          }
        }
      } else if (e.touches.length === 1 && last.touches.length === 1) {
        const dx = e.touches[0].clientX - last.touches[0].x;
        const dy = e.touches[0].clientY - last.touches[0].y;
        nextTranslate = {
          x: last.translate.x + dx,
          y: last.translate.y + dy,
        };
      }

      setScale(nextScale);
      setTranslate(nextTranslate);
      lastRef.current = {
        touches: Array.from(e.touches).map((touch) => ({ x: touch.clientX, y: touch.clientY })),
        scale: nextScale,
        translate: nextTranslate,
      };
    };

    const touchEnd = (e) => {
      if (e.touches.length > 0) {
        lastRef.current = {
          ...lastRef.current,
          touches: Array.from(e.touches).map((touch) => ({ x: touch.clientX, y: touch.clientY })),
        };
      }
    };

    el.addEventListener('touchstart', touchStart);
    el.addEventListener('touchmove', touchMove, { passive: false });
    el.addEventListener('touchend', touchEnd);
    el.addEventListener('touchcancel', touchEnd);

    return () => {
      el.removeEventListener('touchstart', touchStart);
      el.removeEventListener('touchmove', touchMove);
      el.removeEventListener('touchend', touchEnd);
      el.removeEventListener('touchcancel', touchEnd);
    };
  }, []);

  const handleDoubleTap = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  const handleResetZoom = () => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  };

  return (
    <div className={`stripe-chart-zoom-container ${className}`.trim()} ref={containerRef}>
      <span className="stripe-chart-zoom-hint">Pinch to zoom, drag to pan · Double-tap to reset</span>
      <div
        ref={innerRef}
        className="stripe-chart-zoom-inner"
        style={{
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transformOrigin: '0 0',
        }}
        onDoubleClick={handleDoubleTap}
      >
        {children}
      </div>
      {(scale !== 1 || translate.x !== 0 || translate.y !== 0) && (
        <button
          type="button"
          className="stripe-chart-zoom-reset"
          onClick={handleResetZoom}
          aria-label="Reset zoom"
        >
          Reset zoom
        </button>
      )}
    </div>
  );
}

function formatAxisLabel(value, granularity) {
  if (!value) return '—';

  if (granularity === 'month') {
    const [year, month] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year, (month || 1) - 1, 1));
    return date.toLocaleString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
  }

  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return value;

  if (granularity === 'week') {
    return parsed.toLocaleString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
  }

  return parsed.toLocaleString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function getGranularityTitle(granularity) {
  if (granularity === 'week') return 'Weekly Signups';
  if (granularity === 'month') return 'Monthly Signups';
  return 'Daily Signups';
}

export function SignupsOverTimeChart({ chartData = [], granularity = 'day', isLoading = false }) {
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const wrapperRef = useRef(null);
  const svgRef = useRef(null);

  const [isFinePointer, setIsFinePointer] = useState(false);
  const [hover, setHover] = useState({
    index: null,
    tooltip: { left: 0, top: 0 },
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      setIsFinePointer(false);
      return;
    }
    setIsFinePointer(window.matchMedia('(pointer: fine)').matches);
  }, []);

  const { pathData, maxVal, labels, points } = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return { pathData: '', maxVal: 0, labels: [], points: [] };
    }

    const width = 800;
    const height = 300;
    const padding = { top: 20, right: 20, bottom: 40, left: 50 };
    const plotWidth = width - padding.left - padding.right;
    const plotHeight = height - padding.top - padding.bottom;
    const totals = chartData.map((item) => item.total || 0);
    const nextMaxVal = Math.max(...totals, 1);

    const xScale = (index) => padding.left + (index / Math.max(1, chartData.length - 1)) * plotWidth;
    const yScale = (value) => padding.top + plotHeight - (value / nextMaxVal) * plotHeight;

    const points = chartData.map((item, index) => ({
      x: xScale(index),
      y: yScale(item.total || 0),
    }));

    const start = points[0];
    const rest = points.slice(1).map((point) => `L ${point.x} ${point.y}`).join(' ');

    return {
      pathData: start ? `M ${start.x} ${start.y} ${rest}` : '',
      maxVal: nextMaxVal,
      labels: chartData.map((item) => formatAxisLabel(item.date, granularity)),
      points,
    };
  }, [chartData, granularity]);

  const hasChartData = Boolean(chartData && chartData.length > 0);

  if (!hasChartData && !isLoading) {
    return (
      <div className="stripe-chart-empty">
        <p>No signup data to display.</p>
        <p className="stripe-chart-empty-hint">New registrations will appear here once data is available.</p>
      </div>
    );
  }

  const width = 800;
  const height = 300;
  const padding = { top: 20, right: 20, bottom: 40, left: 50 };
  const modalTitle = getGranularityTitle(granularity);

  const handleMouseMove = (e) => {
    if (!hasChartData || isLoading) return;
    if (!isFinePointer) return;
    if (!wrapperRef.current || !svgRef.current) return;
    if (!points || points.length === 0) return;

    const svgRect = svgRef.current.getBoundingClientRect();
    const wrapperRect = wrapperRef.current.getBoundingClientRect();

    // preserveAspectRatio="xMidYMid meet": viewBox is scaled uniformly and centered.
    const scale = Math.min(svgRect.width / width, svgRect.height / height);
    if (!Number.isFinite(scale) || scale <= 0) return;

    const drawWidth = width * scale;
    const drawHeight = height * scale;
    const offsetX = (svgRect.width - drawWidth) / 2;
    const offsetY = (svgRect.height - drawHeight) / 2;

    const xInSvg = e.clientX - svgRect.left - offsetX;
    const yInSvg = e.clientY - svgRect.top - offsetY;

    // Only activate hover inside the actually drawn SVG area.
    if (xInSvg < 0 || xInSvg > drawWidth || yInSvg < 0 || yInSvg > drawHeight) {
      setHover((prev) => ({ ...prev, index: null }));
      return;
    }
    const viewBoxX = xInSvg / scale;

    const plotWidth = width - padding.left - padding.right;
    const n = points.length;
    const xRatio = (viewBoxX - padding.left) / Math.max(1, plotWidth);
    const idx = Math.round(xRatio * (n - 1));
    const clampedIndex = Math.min(n - 1, Math.max(0, idx));

    // Clamp tooltip horizontally so it never pushes content outside the wrapper.
    const halfTooltipWidth = 110; // matches our CSS min/max (approx)
    const tooltipLeftRaw = e.clientX - wrapperRect.left;
    const tooltipLeft = Math.min(
      wrapperRect.width - halfTooltipWidth,
      Math.max(halfTooltipWidth, tooltipLeftRaw)
    );

    setHover({
      index: clampedIndex,
      tooltip: {
        // Client-space anchor for portaled fixed tooltip; horizontal position clamped to wrapper.
        left: wrapperRect.left + tooltipLeft,
        top: e.clientY,
      },
    });
  };

  const handleMouseLeave = () => {
    setHover((prev) => ({ ...prev, index: null }));
  };

  const chartContent = (
    <>
      {hasChartData ? (
        <svg
          ref={svgRef}
          viewBox={`0 0 ${width} ${height}`}
          className="stripe-chart-svg"
          preserveAspectRatio="xMidYMid meet"
        >
        {[0.25, 0.5, 0.75, 1].map((pct) => (
          <line
            key={pct}
            x1={padding.left}
            y1={padding.top + (1 - pct) * (height - padding.top - padding.bottom)}
            x2={width - padding.right}
            y2={padding.top + (1 - pct) * (height - padding.top - padding.bottom)}
            className="stripe-chart-grid"
          />
        ))}

        {[0, 0.25, 0.5, 0.75, 1].map((pct) => (
          <text
            key={pct}
            x={padding.left - 8}
            y={padding.top + (1 - pct) * (height - padding.top - padding.bottom) + 4}
            className="stripe-chart-axis-label"
            textAnchor="end"
          >
            {Math.round(maxVal * pct).toLocaleString()}
          </text>
        ))}

        {labels.length > 0 && (
          <>
            <text x={padding.left} y={height - 8} className="stripe-chart-axis-label" textAnchor="start">
              {labels[0]}
            </text>
            <text x={width / 2} y={height - 8} className="stripe-chart-axis-label" textAnchor="middle">
              {labels[Math.floor(labels.length / 2)]}
            </text>
            <text x={width - padding.right} y={height - 8} className="stripe-chart-axis-label" textAnchor="end">
              {labels[labels.length - 1]}
            </text>
          </>
        )}

        <path
          d={pathData}
          fill="none"
          stroke="#8b0000"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="stripe-chart-line"
        />

        {hover.index != null && points[hover.index] && (
          <>
            <line
              x1={points[hover.index].x}
              y1={padding.top}
              x2={points[hover.index].x}
              y2={height - padding.bottom}
              className="stripe-chart-hover-line"
            />
            <circle
              cx={points[hover.index].x}
              cy={points[hover.index].y}
              r="4"
              className="stripe-chart-hover-marker"
            />
          </>
        )}
        </svg>
      ) : (
        <div className="stripe-chart-empty">
          <p>No signup data to display.</p>
          <p className="stripe-chart-empty-hint">New registrations will appear here once data is available.</p>
        </div>
      )}
    </>
  );

  return (
    <>
      <div
        ref={wrapperRef}
        className={`stripe-chart-wrapper stripe-chart-clickable${isLoading ? ' stripe-chart-wrapper--loading' : ''}`}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={() => {
          if (isLoading) return;
          setChartModalOpen(true);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (isLoading) return;
          if (e.key === 'Enter') setChartModalOpen(true);
        }}
        aria-label="View signup chart larger"
      >
        {chartContent}
        {hasChartData && !isLoading && isFinePointer && hover.index != null && chartData[hover.index] &&
          ReactDOM.createPortal(
            <div
              className="stripe-chart-hover-tooltip"
              style={{ left: hover.tooltip.left, top: hover.tooltip.top }}
            >
              <div className="stripe-chart-hover-tooltip-title">
                {chartData[hover.index].date}
              </div>
              <div className="stripe-chart-hover-tooltip-body">
                Signups: {typeof chartData[hover.index].total === 'number' ? chartData[hover.index].total.toLocaleString() : '0'}
                <span className="stripe-chart-hover-tooltip-subtle">
                  ({formatAxisLabel(chartData[hover.index].date, granularity)})
                </span>
              </div>
            </div>,
            document.body
          )}
        {isLoading && (
          <div className="stripe-chart-loading-overlay" aria-label="Loading chart data">
            <div className="stripe-chart-loading-spinner">
              <div className="loading-spinner" />
            </div>
            <p className="stripe-chart-loading-text">Updating chart…</p>
          </div>
        )}
        <span className="stripe-chart-tap-hint">Tap to view larger</span>
      </div>
      <RevoltModal
        isOpen={chartModalOpen}
        onClose={() => setChartModalOpen(false)}
        title={modalTitle}
        containerClassName="stripe-chart-modal"
      >
        <ZoomPanChart className="stripe-chart-modal-content">
          {chartContent}
        </ZoomPanChart>
      </RevoltModal>
    </>
  );
}
